<template>
  <div class="dark-bank">
    <!-- 装饰性背景 -->
    <div class="bg-gradient"></div>
    
    <div class="container">
      <!-- 头部全网数据 -->
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-label">全网用户</div>
          <div class="stat-value">{{ (systemData.userCount) }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">总存款</div>
          <div class="stat-value">${{ (systemData.totalDeposit) }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">BTL价格</div>
          <div class="stat-value">${{ (systemData.price) }}</div>
        </div>
      </div>

      <!-- 利率卡片 -->
      <div class="rates-bar">
        <div class="rate-item"><span>1月</span><strong>{{ systemData.rate_month_1 }}%</strong></div>
        <div class="rate-item"><span>3月</span><strong>{{ systemData.rate_month_3 }}%</strong></div>
        <div class="rate-item"><span>6月</span><strong>{{ systemData.rate_month_6 }}%</strong></div>
        <div class="rate-item"><span>12月</span><strong>{{ systemData.rate_month_12 }}%</strong></div>
      </div>

      <!-- 用户信息卡片 -->
      <div class="user-card">
        <div class="user-header">
          <div class="user-id">ID: {{ userData.id }}</div>
          <div class="referrer">推荐人: {{ userData.father }}</div>
          <div class="address">{{ shortAddr(userData.addr) }}</div>
        </div>
        <div class="user-stats">
          <div class="user-stat"><span>团队人数</span><strong>{{ userData.teamCount }}</strong></div>
          <div class="user-stat"><span>存款笔数</span><strong>{{ userData.depositCount }}</strong></div>
          <div class="user-stat"><span>直推业绩</span><strong>${{ (userData.performance) }}</strong></div>
          <div class="user-stat"><span>团队奖励</span><strong>${{ (userData.teamProfit) }}</strong></div>
        </div>
        <div class="user-balance">
          <span>总存入资产</span>
          <strong>${{ (userData.deposit) }} <span class="cny">≈ ¥{{ (userData.deposit * 7.2) }}</span></strong>
        </div>
      </div>

      <!-- 新增存款 -->
      <div class="add-deposit">
        <div class="add-title">➕ 新增存款</div>
        <div class="add-controls">
          <input type="number" v-model="newDepositAmount" placeholder="金额 (USDT)" class="amount-input">
          <div class="type-selector">
            <button v-for="t in depositTypes" :key="t.months" 
              :class="['type-btn', { active: newDepositType === t.months }]"
              @click="newDepositType = t.months">
              {{ t.months }}月 <br> {{ getRate(t.months) }}%
            </button>
          </div>
          <button @click="addDeposit" class="submit-btn">存入资金</button>
        </div>
      </div>

      <!-- 存款列表 -->
      <div class="deposit-list">
        <div class="list-header">
          <span>我的存款列表</span>
          <span class="hint">点击取息 · 长按取本金</span>
        </div>
        
        <div v-for="deposit in deposits" :key="deposit.id" class="deposit-item" :class="{ inactive: deposit.status === 1 }">
          <div class="deposit-info">
            <div class="deposit-amount">${{ (deposit.deposit) }} USDT</div>
            <div class="deposit-meta">
              <span class="badge">{{ deposit.type }}个月期</span>
              <span class="badge rate-badge">{{ getRate(deposit.type) }}%</span>
              <span class="date">存入: {{ formatTime(deposit.createTime) }}</span>
            </div>
            <div class="profit-info" v-if="deposit.status === 0">
              <span>已产生利息: ${{ calculateInterest(deposit) }}</span>
              <span v-if="deposit.takeTime > 0" class="mature">已到期可提取</span>
              <span v-else class="mature-date">到期: {{ formatTime(deposit.takeTime) }}</span>
            </div>
            <div class="status-badge" v-if="deposit.status === 1">
              ⚡ 本金已提取
            </div>
          </div>
          <div class="deposit-actions" v-if="deposit.status === 0">
            <button @click="takeInterest(deposit)" class="action-btn interest">💰 取息</button>
            <button @click="takePrincipal(deposit)" class="action-btn principal" longpress>🏦 取本金</button>
          </div>
        </div>

        <div v-if="deposits.length === 0" class="empty-state">
          ✨ 暂无存款，请存入一笔开始生息 ✨
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'

export default {
  name: 'DarkBank',
  setup() {
    // 系统数据
    const systemData = reactive({
      userCount: 1248,
      price: 245,      // 1 BTL = 24500 USDT
      rate_month_1: 2,
      rate_month_3: 9,
      rate_month_6: 24,
      rate_month_12: 60,
      totalDeposit: 2458
    })

    // 用户数据
    const userData = reactive({
      id: 10086,
      father: 10001,
      teamCount: 28,
      depositCount: 2,
      performance: 12500,
      teamProfit: 3420,
      deposit: 17000,
      addr: '0x71C7...970F'
    })

    // 存款列表
    const deposits = ref([])
    const newDepositAmount = ref(1000)
    const newDepositType = ref(3)

    const depositTypes = [
      { months: 1, rateKey: 'rate_month_1' },
      { months: 3, rateKey: 'rate_month_3' },
      { months: 6, rateKey: 'rate_month_6' },
      { months: 12, rateKey: 'rate_month_12' }
    ]

    // 获取利率
    const getRate = (months) => {
      const map = {
        1: systemData.rate_month_1,
        3: systemData.rate_month_3,
        6: systemData.rate_month_6,
        12: systemData.rate_month_12
      }
      return map[months] || 0
    }

    // 计算利息 (简单单利)
    const calculateInterest = (deposit) => {
      if (deposit.status === 1) return 0
      const now = Math.floor(Date.now() / 1000)
      const start = deposit.createTime
      const months = deposit.type
      const totalSeconds = months * 30 * 24 * 3600
      const elapsed = Math.min(totalSeconds, Math.max(0, now - start))
      const ratio = elapsed / totalSeconds
      const rate = getRate(deposit.type) / 100
      const interest = deposit.deposit * rate * ratio
      return Math.floor(interest * 100) / 100
    }

    // 模拟生成时间 (相对当前)
    const randomTimeAgo = (daysAgo) => {
      return Math.floor(Date.now() / 1000) - daysAgo * 86400
    }

    // 初始化示例存款
    const initDeposits = () => {
      const now = Math.floor(Date.now() / 1000)
      deposits.value = [
        {
          id: 10001,
          createTime: randomTimeAgo(15),
          takeTime: randomTimeAgo(-15), // 15天后到期
          deposit: 5000,
          type: 1,
          status: 0
        },
        {
          id: 10002,
          createTime: randomTimeAgo(45),
          takeTime: randomTimeAgo(-45),
          deposit: 12000,
          type: 3,
          status: 0
        }
      ]
      userData.depositCount = deposits.value.length
      userData.deposit = deposits.value.reduce((sum, d) => sum + d.deposit, 0)
      systemData.totalDeposit = 2458000 + userData.deposit
    }

    // 添加存款
    const addDeposit = () => {
      let amount = parseFloat(newDepositAmount.value)
      if (isNaN(amount) || amount <= 0) {
        alert('请输入有效金额')
        return
      }
      const now = Math.floor(Date.now() / 1000)
      const months = newDepositType.value
      const takeTime = now + months * 30 * 86400
      
      const newId = Date.now() % 100000
      deposits.value.push({
        id: newId,
        createTime: now,
        takeTime: takeTime,
        deposit: amount,
        type: months,
        status: 0
      })
      
      // 更新用户统计
      userData.depositCount = deposits.value.length
      userData.deposit += amount
      systemData.totalDeposit += amount
      newDepositAmount.value = 1000
    }

    // 取利息 (累加利息至团队奖励或余额概念，这里模拟弹窗)
    const takeInterest = (deposit) => {
      const interest = calculateInterest(deposit)
      if (interest <= 0) {
        alert('当前无利息可提取或未到期')
        return
      }
      // 模拟利息发放到余额 (更新团队奖励作为示意)
      userData.teamProfit += interest
      alert(`成功提取利息 $${interest.toFixed(2)} USDT，已加入团队奖励余额`)
    }

    // 取本金 (标记状态，扣除用户总存款)
    const takePrincipal = (deposit) => {
      if (deposit.status === 1) {
        alert('本金已提取过')
        return
      }
      const now = Math.floor(Date.now() / 1000)
      if (now < deposit.takeTime) {
        alert(`未到期无法提取本金，到期日 ${formatTime(deposit.takeTime)}`)
        return
      }
      deposit.status = 1
      userData.deposit -= deposit.deposit
      userData.depositCount = deposits.value.filter(d => d.status === 0).length
      alert(`本金 $${deposit.deposit} USDT 已提取至钱包`)
    }

    // 辅助函数
    const formatNumber = (num) => {
      if (num === undefined) return '0'
      return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }

    const formatTime = (timestamp) => {
      if (!timestamp) return '--'
      const date = new Date(timestamp * 1000)
      return `${date.getMonth()+1}/${date.getDate()} ${date.getHours()}:${date.getMinutes().toString().padStart(2,'0')}`
    }

    const shortAddr = (addr) => {
      if (!addr) return ''
      return addr.length > 10 ? addr.slice(0,6)+'...'+addr.slice(-4) : addr
    }

    onMounted(() => {
      initDeposits()
    })

    return {
      systemData,
      userData,
      deposits,
      newDepositAmount,
      newDepositType,
      depositTypes,
      getRate,
      calculateInterest,
      addDeposit,
      takeInterest,
      takePrincipal,
      formatNumber,
      formatTime,
      shortAddr
    }
  }
}
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.dark-bank {
  min-height: 100vh;
  background: #0a0a0f;
  position: relative;
  padding: 20px 16px 40px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.bg-gradient {
  position: fixed;
  top: -20%;
  left: -20%;
  width: 140%;
  height: 140%;
  background: radial-gradient(circle at 30% 10%, rgba(0, 255, 255, 0.08), transparent 70%);
  pointer-events: none;
}

.container {
  max-width: 600px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
}

/* 全网统计卡片 */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.stat-card {
  background: rgba(20, 20, 30, 0.7);
  backdrop-filter: blur(4px);
  border-radius: 28px;
  padding: 14px 8px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 20px rgba(0,0,0,0.4);
}

.stat-label {
  font-size: 12px;
  color: #9ca3af;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}

.stat-value {
  font-size: 20px;
  font-weight: 700;
  background: linear-gradient(135deg, #fff, #a0f0ff);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

/* 利率条 */
.rates-bar {
  display: flex;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 48px;
  padding: 8px 12px;
  margin-bottom: 24px;
  justify-content: space-between;
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255,215,0,0.2);
}

.rate-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.rate-item span {
  font-size: 11px;
  color: #aaa;
}

.rate-item strong {
  font-size: 18px;
  font-weight: 700;
  color: #f5c542;
}

/* 用户卡片 */
.user-card {
  background: linear-gradient(145deg, #111118, #0c0c12);
  border-radius: 36px;
  padding: 20px;
  margin-bottom: 24px;
  border: 1px solid rgba(255,255,255,0.05);
  box-shadow: 0 15px 35px rgba(0,0,0,0.5);
}

.user-header {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 13px;
  color: #88aaff;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(255,255,255,0.1);
  margin-bottom: 16px;
}

.user-id, .referrer, .address {
  background: rgba(100, 140, 255, 0.12);
  padding: 4px 12px;
  border-radius: 40px;
  font-weight: 500;
}

.user-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.user-stat {
  background: rgba(0,0,0,0.4);
  border-radius: 20px;
  padding: 10px 4px;
  text-align: center;
}

.user-stat span {
  display: block;
  font-size: 10px;
  color: #7e8493;
  margin-bottom: 6px;
}

.user-stat strong {
  font-size: 16px;
  color: white;
}

.user-balance {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  background: rgba(0,200,255,0.08);
  padding: 14px 18px;
  border-radius: 28px;
  margin-top: 4px;
}

.user-balance span {
  font-size: 14px;
  color: #aab2c4;
}

.user-balance strong {
  font-size: 22px;
  font-weight: 800;
  color: #b9f6ff;
}

.cny {
  font-size: 12px;
  color: #6c86a3;
  margin-left: 6px;
}

/* 新增存款区 */
.add-deposit {
  background: rgba(18, 20, 28, 0.9);
  border-radius: 32px;
  padding: 18px;
  margin-bottom: 28px;
  backdrop-filter: blur(4px);
}

.add-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #ddd;
}

.add-controls {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.amount-input {
  background: #1e1f2a;
  border: 1px solid #2f3342;
  border-radius: 60px;
  padding: 14px 20px;
  font-size: 16px;
  color: white;
  outline: none;
  transition: 0.2s;
}

.amount-input:focus {
  border-color: #3b82f6;
}

.type-selector {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.type-btn {
  flex: 1;
  background: #1e1f2a;
  border: none;
  padding: 10px 0;
  border-radius: 40px;
  color: #bbb;
  font-weight: 500;
  transition: 0.2s;
  cursor: pointer;
}

.type-btn.active {
  background: #2c3e66;
  color: white;
  box-shadow: 0 2px 8px rgba(59,130,246,0.3);
}

.submit-btn {
  background: linear-gradient(95deg, #2a6f8f, #1e4a6e);
  border: none;
  padding: 14px;
  border-radius: 48px;
  font-weight: bold;
  font-size: 16px;
  color: white;
  cursor: pointer;
  transition: 0.2s;
}

.submit-btn:hover {
  transform: scale(0.98);
  background: linear-gradient(95deg, #1e5a7a, #12405c);
}

/* 存款列表 */
.deposit-list {
  background: rgba(10, 12, 18, 0.7);
  border-radius: 36px;
  padding: 6px 4px;
}

.list-header {
  display: flex;
  justify-content: space-between;
  padding: 16px 18px 8px;
  font-size: 14px;
  color: #b9c7d9;
  font-weight: 500;
}

.hint {
  font-size: 11px;
  background: #1a1e2c;
  padding: 4px 10px;
  border-radius: 40px;
  color: #ffd966;
}

.deposit-item {
  background: #12141e;
  margin: 12px 12px;
  border-radius: 28px;
  padding: 18px;
  transition: 0.2s;
  border-left: 3px solid #3b82f6;
}

.deposit-item.inactive {
  opacity: 0.6;
  border-left-color: #6c757d;
  background: #0d0f16;
}

.deposit-amount {
  font-size: 22px;
  font-weight: 700;
  color: #fffaec;
  margin-bottom: 8px;
}

.deposit-meta {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.badge {
  background: #252a3a;
  padding: 4px 12px;
  border-radius: 60px;
  font-size: 11px;
  color: #b0c4ff;
}

.rate-badge {
  background: #3b2e1e;
  color: #ffbf69;
}

.date {
  font-size: 11px;
  color: #8f9bb3;
  align-self: center;
}

.profit-info {
  display: flex;
  gap: 12px;
  align-items: baseline;
  font-size: 13px;
  color: #6fcf97;
  margin-top: 6px;
}

.mature-date {
  color: #ffa25b;
  font-size: 11px;
}

.status-badge {
  margin-top: 12px;
  font-size: 13px;
  background: #2a2e3c;
  display: inline-block;
  padding: 4px 14px;
  border-radius: 30px;
}

.deposit-actions {
  display: flex;
  gap: 12px;
  margin-top: 18px;
}

.action-btn {
  flex: 1;
  padding: 10px 0;
  border-radius: 60px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  font-size: 14px;
  transition: 0.15s;
}

.interest {
  background: #1f3a3f;
  color: #9effcf;
}

.interest:active {
  background: #0e2a2f;
}

.principal {
  background: #3c2a2a;
  color: #ffaa88;
}

.principal:active {
  background: #2e1f1f;
}

.empty-state {
  text-align: center;
  padding: 48px 20px;
  color: #6c7a8e;
  font-size: 15px;
}

/* 长按提示简单模拟，未做原生长按，但按钮文案示意 */
.action-btn.principal {
  position: relative;
}
</style>